import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../contexts/AppContext";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Heart, Play, ShoppingBag, Share2, Check } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { trackProductView, toggleFavorite, subscribeUserFavoriteIds } from "../services/analytics";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { t, formatPrice, pickField } = useApp();
  const { add } = useCart();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [favIds, setFavIds] = useState([]);
  const [shared, setShared] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const ctaRef = useRef(null);

  useEffect(() => {
    setData(null);
    setActiveImageIdx(0);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      if (r.data?.product?.id) trackProductView(r.data.product.id);
    });
  }, [slug]);

  useEffect(() => {
    if (!user) { setFavIds([]); return; }
    return subscribeUserFavoriteIds(user.uid, setFavIds);
  }, [user]);

  // Mobil yapışqan "Səbətə əlavə et" paneli — istifadəçi az miqdar
  // (~200px) aşağı sürüşən kimi görünür, yuxarı qalxanda yox olur.
  useEffect(() => {
    if (!data) return;
    const onScroll = () => {
      // 200px-dən çox aşağı sürüşülübsə paneli göstər.
      setShowStickyBar(window.scrollY > 200);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [data]);

  // Sticky bar aktiv olanda body-yə class qoy ki, AIChat və AmbientPlayer
  // mobil görünüşdə müvəqqəti gizlənsin.
  useEffect(() => {
    if (showStickyBar) {
      document.body.classList.add("mobile-sticky-cta");
    } else {
      document.body.classList.remove("mobile-sticky-cta");
    }
    return () => document.body.classList.remove("mobile-sticky-cta");
  }, [showStickyBar]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[var(--text-2)]">{t.common.loading}</div>;
  const { product, artist, related } = data;
  const isFav = favIds.includes(product.id);

  const onFav = async () => {
    if (!user) { window.location.href = "/login?next=" + encodeURIComponent(`/product/${slug}`); return; }
    await toggleFavorite(user.uid, product.id);
  };

  const onShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const title = pickField(product, "title") || product.title || "";
    const text = artist?.name ? `${artist.name} — ${title}` : title;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    } catch {
      // last-resort fallback
      window.prompt("Linki kopyalayın:", url);
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12" data-testid="product-detail">
      <div className="metadata mb-2">{t.product.breadcrumbCatalog} <span className="mx-2">›</span> {(pickField(data.category, "name") || data.category?.name || "").toUpperCase()} <span className="mx-2">›</span> {(pickField(product, "title") || product.title).toUpperCase()}</div>

      <div className="grid md:grid-cols-12 gap-10 mt-6">
        {/* IMAGE COLUMN */}
        <div className="md:col-span-6">
          {(() => {
            // Gallery: use product.images if available, otherwise fall back to cover_url
            const gallery = Array.isArray(product.images) && product.images.length
              ? product.images
              : (product.cover_url ? [product.cover_url] : []);
            const activeUrl = gallery[activeImageIdx] || product.cover_url;
            return (
              <>
                <div className="card-flat aspect-square zoom-wrap relative">
                  <img src={activeUrl} alt={pickField(product, "title") || product.title} className="w-full h-full object-cover" />
                  {product.limited && <div className="absolute top-4 left-4 metadata bg-[var(--bg)]/80 px-2 py-1 border border-[var(--border)]">{t.product.limited}</div>}
                  {product.is_used && (
                    <div className="absolute top-4 right-4 metadata bg-[var(--bg)]/80 px-2 py-1 border border-[var(--accent)] text-[var(--accent)]" data-testid="used-badge">
                      {t.product.secondHand || "İKİNCİ ƏL"}
                    </div>
                  )}
                </div>
                {gallery.length > 1 && (
                  <div className="grid grid-cols-5 gap-2 mt-3" data-testid="product-gallery-thumbs">
                    {gallery.map((url, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImageIdx(idx)}
                        className={`aspect-square border-2 transition-all ${idx === activeImageIdx ? "border-[var(--accent)] ring-1 ring-[var(--accent)]/30" : "border-[var(--border)] hover:border-[var(--text-2)]"}`}
                        data-testid={`product-gallery-thumb-${idx}`}
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
          {(() => {
            // "Boş" sayılan dəyərlər: null/undefined/boş string/yalnız boşluq/
            // və admin tərəfindən qalmış default placeholder-lər ("—", "0", "-").
            const isBlank = (v) => {
              if (v == null) return true;
              const s = String(v).trim().toLowerCase();
              return s === "" || s === "—" || s === "-" || s === "0" || s === "n/a" || s === "na";
            };
            const specs = [
              [t.product.catNo, product.catalog_no],
              [t.product.format, product.format],
              [t.product.rpm, product.rpm],
              [t.product.weight, product.weight],
            ].filter(([, v]) => !isBlank(v));
            if (!specs.length) return null;
            return (
              <div
                className="mt-6 border-t border-b border-[var(--border)] py-5"
                data-testid="product-specs"
              >
                <div
                  className="grid gap-x-6 gap-y-5"
                  style={{
                    gridTemplateColumns: `repeat(${Math.min(specs.length, 4)}, minmax(0, 1fr))`,
                  }}
                >
                  {specs.map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                        {label}
                      </div>
                      <div className="mt-2 font-mono text-[13px] text-[var(--text)] break-words leading-snug">
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* INFO COLUMN */}
        <div className="md:col-span-6">
          {artist && (
            <Link to={`/artist/${artist.id || artist.slug}`} className="metadata link-underline">{((pickField(artist, "name") || artist.name || "") + "").toUpperCase()}</Link>
          )}
          <h1 className="product-title text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight" data-testid="product-title">{pickField(product, "title") || product.title}</h1>
          <p className="text-[var(--text-2)] mt-6 leading-relaxed max-w-xl whitespace-pre-line">{pickField(product, "description") || product.description}</p>

          {product.is_used && (pickField(product, "condition") || product.condition) && (
            <div className="mt-8 border-l-2 border-[var(--accent)] pl-4 py-2 bg-[var(--surface)]/30" data-testid="condition-section">
              <div className="metadata text-[var(--accent)] mb-2">{t.product.condition || "VƏZİYYƏT"}</div>
              <p className="text-[var(--text-2)] leading-relaxed whitespace-pre-line">{pickField(product, "condition") || product.condition}</p>
            </div>
          )}

          <div className="flex items-center gap-8 mt-8">
            <div>
              <div className="metadata">{t.product.releaseDate}</div>
              <div className="font-mono mt-1">{product.release_date}</div>
            </div>
            <div>
              <div className="metadata">{t.product.price}</div>
              {(() => {
                const pct = Math.max(0, Math.min(90, Number(product.discount_pct) || 0));
                if (pct > 0) {
                  const finalP = Number(product.price_azn) * (1 - pct / 100);
                  return (
                    <div className="mt-1 flex items-baseline gap-3" data-testid="product-detail-price">
                      <span className="font-display text-3xl text-[var(--accent)]">{formatPrice(finalP)}</span>
                      <span className="font-mono text-sm line-through text-[var(--text-muted)]">{formatPrice(product.price_azn)}</span>
                      <span className="metadata bg-[var(--accent)] text-white px-2 py-0.5">-{pct}%</span>
                    </div>
                  );
                }
                return <div className="font-display text-3xl mt-1" data-testid="product-detail-price">{formatPrice(product.price_azn)}</div>;
              })()}
            </div>
          </div>

          {/* TRACKLIST */}
          {product.tracklist?.length > 0 && (
            <div className="mt-10">
              <div className="metadata mb-3">{t.sections.tracklist}</div>
              <ul className="border-t border-[var(--border)]">
                {product.tracklist.map((tr, i) => (
                  <li key={i} className="flex items-center justify-between py-3 border-b border-[var(--border-soft)]" data-testid={`track-${i}`}>
                    <span className="flex items-center gap-4">
                      <span className="font-mono text-xs text-[var(--text-muted)]">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display text-lg">{tr.title}</span>
                    </span>
                    <span className="flex items-center gap-3 font-mono text-xs text-[var(--text-2)]">
                      {tr.duration}
                      <button className="w-7 h-7 rounded-full border border-[var(--border)] flex items-center justify-center hover:border-[var(--primary)] hover:text-[var(--primary)]"><Play size={11} /></button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* SPOTIFY EMBED — always-expanded album view (like Spotify's own site) */}
          {(() => {
            const isValidId = (v) => typeof v === "string" && /^[a-zA-Z0-9]{22}$/.test(v);
            let albumId = isValidId(product.spotify_album_id) ? product.spotify_album_id : "";
            if (!albumId && product.spotify_album_url) {
              const s = String(product.spotify_album_url);
              // Skip search URLs — they are not embeddable
              if (!/\/search\//i.test(s)) {
                // Accept full URL, embed URL, /intl-XX/album/ paths, spotify:album:id, or the ID itself.
                // Query strings (?si=…) are ignored.
                const m = s.match(/[/]album[/]([a-zA-Z0-9]{22})/)
                       || s.match(/spotify:album:([a-zA-Z0-9]{22})/)
                       || s.match(/^([a-zA-Z0-9]{22})$/);
                if (m) albumId = m[1];
              }
            }
            const trackId = isValidId(product.spotify_track_id) ? product.spotify_track_id : "";
            const type = albumId ? "album" : (trackId ? "track" : null);
            const id = albumId || trackId;
            if (!type || !id) return null;
            // Full-height "expanded" style: albom → 620px shows cover + track list.
            // Single track → 232px shows compact player. Both use theme=0 (dark).
            const height = type === "album" ? 620 : 232;
            return (
              <div className="mt-10 pt-8 border-t border-[var(--border)]" data-testid="spotify-embed">
                <div className="flex items-center gap-2 mb-4">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="#1DB954" aria-hidden>
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.42-1.021.6-1.56.3z"/>
                  </svg>
                  <div className="metadata text-[var(--text)]">{t.product.spotifyPreview}</div>
                </div>
                <div className="bg-[#121212] p-1 shadow-lg" style={{ borderRadius: 14 }}>
                  <iframe
                    title="Spotify Player"
                    src={`https://open.spotify.com/embed/${type}/${id}?theme=0`}
                    width="100%"
                    height={height}
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    style={{ border: 0, borderRadius: 12, display: "block" }}
                  />
                </div>
                <a
                  href={product.spotify_album_url || `https://open.spotify.com/${type}/${id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] text-[#1DB954] hover:text-[#25e067] mt-3 transition-colors"
                >
                  SPOTIFY-DƏ AÇ →
                </a>
              </div>
            );
          })()}

          {/* CTA */}
          <div ref={ctaRef} className="flex flex-wrap gap-4 mt-10">
            <button className="btn-primary" onClick={() => add({ ...product, artist_name: artist?.name })} data-testid="add-to-cart-btn">
              <ShoppingBag size={14} /> {t.common.addToCart}
            </button>
            <button className={`btn-outline ${isFav ? "text-[var(--accent)] border-[var(--accent)]" : ""}`} onClick={onFav} data-testid="wishlist-btn" aria-label={isFav ? t.fav.remove : t.fav.add}>
              <Heart size={14} fill={isFav ? "currentColor" : "none"} /> {isFav ? t.fav.remove : t.fav.add}
            </button>
            <button
              className="btn-outline"
              onClick={onShare}
              data-testid="share-btn"
              aria-label="Paylaş"
              title="Paylaş"
            >
              {shared ? <Check size={14} /> : <Share2 size={14} />}
              {shared ? "Kopyalandı" : "Paylaş"}
            </button>
          </div>
        </div>
      </div>

      {/* RELATED */}
      {related?.length > 0 && (
        <section className="mt-24">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display text-4xl md:text-5xl">{t.sections.relatedReleases}</h2>
            <Link to="/catalog" className="metadata hover:text-[var(--primary)]">{t.sections.viewCatalog} →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {related.map((p) => <ProductCard key={p.id} product={p} artistName={pickField(artist, "name") || artist?.name} artistSlug={artist?.slug} artistId={artist?.id} />)}
          </div>
        </section>
      )}

      {/* Mobil yapışqan səbət paneli — yalnız mobil, aşağı sürüşdükdə görünür */}
      <div
        className={`md:hidden fixed left-0 right-0 bottom-0 z-[60] border-t border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur-md px-4 py-3 transition-all duration-300 ease-out ${
          showStickyBar ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
        }`}
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        data-testid="mobile-sticky-cta"
        aria-hidden={!showStickyBar}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 shrink-0 overflow-hidden border border-[var(--border)]">
            <img src={product.cover_url} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="metadata text-[var(--text-muted)] truncate text-[10px]">{(artist?.name || "").toUpperCase()}</div>
            <div className="text-sm font-medium truncate leading-tight">{pickField(product, "title") || product.title}</div>
            <div className="text-[12px] font-mono text-[var(--primary)] mt-0.5">{formatPrice(product.price_azn)}</div>
          </div>
          <button
            className="btn-primary !px-4 !py-3 whitespace-nowrap shrink-0"
            onClick={() => add({ ...product, artist_name: artist?.name })}
            data-testid="sticky-add-to-cart-btn"
          >
            <ShoppingBag size={14} /> {t.common.addToCart}
          </button>
        </div>
      </div>
    </div>
  );
}
